<template>
	<FauxModal
		class="social-login"
		:disable-logo-href="route.name === 'api-connection'"
		:org-appearance-logo="logoUrl"
		:org-name="orgName"
		:logo-without-text="logoWithoutText"
		:resources="resources"
	>
		<div v-if="isLoading" class="p-5 text-center">
			<Inline svg="loading" class="spin absolute" />
		</div>

		<template v-if="!isWithinFrame">
			<CAuthPageHeader
				v-if="pageTitle && !isLoading"
				:title="pageTitle"
				:back-button="route.meta.showBackButton"
			/>
			<div v-if="route.query.source == 'square'" class="sz-2 text-center bg-info border border-info p-3 mb-2">
				<p>Connect your Square account to Cognito Forms.</p>
			</div>
			<div v-if="showPlanRestrictionMessage" class="sz-2 text-center text-error bg-warning p-3 mb-2">
				<p>Please upgrade to a Pro plan to enable this feature.</p>
			</div>
		</template>

		<AuthWithinFrame
			v-if="isWithinFrame"
			:page-source="pageSource"
		>
			<template #button-text>
				<span v-if="pageSource === 'login'">Log into</span>
				<span v-else-if="pageSource === 'signup'">Sign Up</span> Cognito Forms
			</template>
			<template #description>
				<p class="text-center">
					Please <span v-if="pageSource === 'login'">login</span>
					<span v-else-if="pageSource === 'signup'">sign up</span> using the other window.
				</p>
			</template>
		</AuthWithinFrame>

		<Transition v-else name="fade" mode="out-in">
			<RouterView
				v-show="!isLoading"
				:prefilled-email="capturedEmail"
				:api-connection="apiConnection"
				:signup-message="signUpMessage"
				@emailCaptured="setEmailAddress"
				@oAuthIntegrationCaptured="setIntegration"
				@oAuthIntegrationRestricted="handleRestriction"
				@title-changed="handleTitleChange"
			>
				<template v-if="botProtection.challenge" #captcha>
					<CCaptcha ref="captcha" :bot-challenge="botProtection.challenge" @validated="captchaValidated" />
				</template>
			</RouterView>
		</Transition>

		<div v-if="showCognitoDisclaimers" class="social-login__tos text-center mt sz-2" @thirdPartyOAuth="toggleCognitoDisclaimers">
			<p><a href="/legal/terms" target="_blank" title="Agree to Terms of Service">Terms of Service</a> | <a href="/legal/privacy" target="_blank" title="Agree to Privacy Policy">Privacy Policy</a></p>
			<p>Copyright &copy; {{ new Date().getFullYear() }} Cognito LLC, All rights reserved. <br>Part of the <strong>Cognito Apps<sup>SM</sup></strong> family of products.</p>
		</div>
		<CAppInsights />
	</FauxModal>
</template>

<script lang="ts">
	import { defineComponent, ref, reactive, computed, provide, onBeforeMount } from 'vue';
	import { enableBotProtection, interceptResponses } from '@cognitoforms/api/services/service-request';
	import CAuthPageHeader from 'src/components/auth/AuthPageHeader.vue';
	import CCaptcha from 'src/components/form-controls/Captcha.vue';
	import { isWithinFrame } from '@cognitoforms/utils/utilities';
	import AuthWithinFrame from 'src/components/auth/AuthWithinFrame.vue';
	import { getMinimumPlan } from '@cognitoforms/api/services/templates-service';
	import { type BotProtection, UserType } from '@cognitoforms/api/services/bot-protection';
	import FauxModal from 'src/layouts/FauxModal.vue';
	import CAppInsights from 'src/components/embeds/AppInsights';
	import { CognitoConfiguration } from '@cognitoforms/utils/clientside-configuration';
	import { updateOrgAppearance, updateFavicon, setPageTitleFromCSS, getOrgName } from 'src/views/admin/utils/org-appearance-utils';
	import { getResources } from 'src/localization/resource-util';
	import type { LocalizationResources } from 'src/localization/resources/localization-resources.generated';
	import { localizationResourcesKey } from 'src/utilities/injection-keys';
	import { useRoute } from 'vue-router/composables';

	interface ApiConnection {
		userCode: string | undefined;
		integrationName: string | null;
	}

	export default defineComponent({
		name: 'Auth',
		components: {
			CAuthPageHeader,
			CCaptcha,
			AuthWithinFrame,
			FauxModal,
			CAppInsights
		},
		async beforeRouteEnter(to, from, next) {
			updateOrgAppearance(CognitoConfiguration.UseSystemColorsEnabled, null, null);
			const { logoLink } = updateFavicon();
			const orgName = getOrgName();

			document.documentElement.setAttribute('data-auth-style', 'app');
			await import('@cognitoforms/styles/app/common/common.scss');

			next((vm: any) => {
				if (logoLink)
					vm.logoUrl = logoLink;
				if (orgName)
					vm.orgName = orgName;
			});
		},
		beforeRouteUpdate(to, from, next) {
			// automatically pass query string parameters between auth routes
			if (!Object.keys(from.query).every(p => to.query[p] === from.query[p]))
				next({ ...to, query: Object.assign({}, from.query, to.query) });
			else {
				next();
			}
		},
		inheritAttrs: false,
		setup() {
			const route = useRoute();

			const captcha = ref<InstanceType<typeof CCaptcha>>();

			const botProtection = ref<BotProtection>({});
			const capturedEmail = ref<string | null>(null);
			const showCognitoDisclaimers = ref<boolean>(false);
			const apiConnection = reactive<ApiConnection>({
				userCode: route.query.user as string | undefined,
				integrationName: (route.query.source as string) || null
			});
			const signUpMessage = ref<string | null>(null);
			const title = ref<string | null>(null);
			const showPlanRestrictionMessage = ref<boolean>(false);
			const isLoading = ref<boolean>(true);
			const resources = ref<LocalizationResources>({});

			const logoUrl = ref<string | null>(null);
			const logoWithoutText = ref<boolean>(false);
			const orgName = ref(null);

			const pageTitle = computed(() => {
				if (route.name === 'api-connection')
					return (apiConnection.integrationName || 'Integration') + ' Authorization';
				return title.value;
			});

			const pageSource = computed(() => {
				return window.location.href.includes('login') ? 'login' : (window.location.href.includes('signup') || route.query.templateName ? 'signup' : '');
			});

			const isWithinFrameComputed = computed(() => isWithinFrame());

			provide(localizationResourcesKey, resources);

			const handleRestriction = (e: { value: boolean }) => {
				showPlanRestrictionMessage.value = e.value;
			};

			const toggleCognitoDisclaimers = () => {
				showCognitoDisclaimers.value = true;
			};

			const captchaValidated = (response: any) => {
				botProtection.value.reportHuman?.(response);
			};

			const ensureCaptcha = async () => {
				captcha.value?.validateUser(true);
			};

			const setEmailAddress = (email: string) => {
				capturedEmail.value = email;
			};

			const setIntegration = (integration: string) => {
				apiConnection.integrationName = integration;
				isLoading.value = false;
			};

			const handleTitleChange = (newTitle: string, logoWithoutTextValue = false) => {
				title.value = newTitle;
				logoWithoutText.value = logoWithoutTextValue;
			};

			onBeforeMount(async () => {
				interceptResponses(async (res: Response) => {
					if (res.status === 400 && (await res.text()) === 'captcha') {
						await ensureCaptcha();
					}
				});

				if (['templates', 'shared-template'].includes(route.query.source as string)) {
					const details = route.query.details as string;
					const orgCode = details.split('-')[0];
					const formId = details.split('-')[1];
					try {
						const response = await getMinimumPlan(orgCode, formId);
						if (response && response.Name !== 'Individual') {
							signUpMessage.value = `We're signing you up for a Free 14-day ${response.DisplayName} trial so you can use all of the features required by this template. No credit card required.`;
						}
					}
					catch (error) {
						console.error('Error getting minimum plan:', error);
					}
				}

				if (route.query.source === 'square') {
					signUpMessage.value = 'We\'re signing you up for a Free 14-day Team trial so you can use all of the features required by this template. No credit card required.';
				}

				botProtection.value = await enableBotProtection();
				botProtection.value.whenActivityDetected?.((bp: any) => {
					if (bp.userType === UserType.Bot) {
						ensureCaptcha();
					}
				});

				const lang = (route.query.lang as string);
				resources.value = await getResources(lang);

				isLoading.value = route.name === 'api-connection';
			});

			return {
				route,
				captcha,
				botProtection,
				capturedEmail,
				showCognitoDisclaimers,
				apiConnection,
				signUpMessage,
				title,
				showPlanRestrictionMessage,
				logoUrl,
				logoWithoutText,
				orgName,
				isLoading,
				resources,
				pageTitle,
				pageSource,
				isWithinFrame: isWithinFrameComputed,
				handleRestriction,
				toggleCognitoDisclaimers,
				captchaValidated,
				ensureCaptcha,
				setEmailAddress,
				setIntegration,
				handleTitleChange
			};
		},
		head: {
			htmlAttrs: {
				class: ['app not-iframe-page']
			},
			titleTemplate: (title) => {
				if (title) {
					if (title.includes('-') || title === 'Cognito Forms')
						return `${title}`;
					else
						return `${title} - Cognito Forms`;
				}
				else {
					// Set page title based on org name set in CSS, if available
					const updatedTitle = setPageTitleFromCSS();
					if (updatedTitle)
						return updatedTitle;
				}
				return 'Cognito Forms - Easily Build Powerful Forms';
			}
		}
	});
</script>

<style lang="scss">
@import 'src/design/color/appearance';

[data-auth-style='web'] .social-login {

	.faux-modal__body {
		@include contain-until($b5);
	}

	.faux-modal__wrapper {
		max-width: 320px;
	}

	a {
		color: var(--text-primary);

		&:hover,
		&:focus,
		&:active {
			color: var(--bg-cta-hovered);
		}
	}

	&__button {
		min-height: 46px;
		background: transparent;
		border: 1px solid var(--border-primary);
		box-shadow: 3px 3px 0 var(--shade-3);
		padding: .6em 1em;
		color: var(--shade-9);
		font-family: var(--font-display);
		text-align: left;
	}

	.facebook {
		width: 20px;
		margin: 2px;

		& > svg [fill] {
			fill: #1877f2; // FB brand blue
		}
	}

	&__guts > *:not(:last-child),
	&__form > * {
		margin-bottom: math.div($gutter, 2);

		.el-input__inner {
			border: 1px solid var(--border-primary);

			&:focus {
				border: 1px solid var(--outline-focused);
			}

			& [data-whatinput='keyboard'] :focus:not(body) {
				box-shadow: none;
			}
		}
	}

	.strike {
		display: block;
		overflow: hidden;
		text-align: center;
		white-space: nowrap;

		& > span {
			display: inline-block;
			position: relative;
			color: var(--shade-6);
			font-weight: 800;
			text-transform: uppercase;

			&::before,
			&::after {
				display: block;
				position: absolute;
				top: calc(50% - 1.5px);
				width: 9999px;
				height: 3px;
				background: var(--border-subtle);
				content: '';
			}

			&::before {
				right: 100%;
				margin-right: 15px;
			}

			&::after {
				left: 100%;
				margin-left: 15px;
			}
		}
	}

	.el-input input,
	.button--primary,
	.button--secondary,
	&__button {
		display: block;
		width: 100%;
		border-radius: 0;
		-webkit-appearance: none;
	}

	.el-input__inner {
		padding: .6em;
		font-size: inherit;
	}

	h1 {
		margin-bottom: 16px;
	}
}
</style>